import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Layout from '../components/Layout';
import {
  Shield,
  Eye,
  Zap,
  Brain,
  GraduationCap,
  CheckCircle,
  ArrowRight,
  Play,
  Star
} from 'lucide-react';

const HomePage = () => {
  const [selectedStrategyIndex, setSelectedStrategyIndex] = useState(null);
  const [stats, setStats] = useState({
    clients: 0,
    threats: 0,
    uptime: 0,
    response: 0
  });

  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, -50]);

  useEffect(() => {
    // Animate stats on mount
    const timer = setTimeout(() => {
      setStats({
        clients: 500,
        threats: 1000000,
        uptime: 99.9,
        response: 15
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleStrategyClick = (index) => {
    setSelectedStrategyIndex(selectedStrategyIndex === index ? null : index);
  };

  // Updated with modern icons and refined content
  const strategies = [
    {
      title: 'AI-Powered Threat Detection',
      detail: 'Our advanced AI algorithms continuously monitor, analyze, and predict cyber threats in real-time, providing unparalleled protection against emerging security risks.',
      icon: <Brain className="h-6 w-6" />,
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Zero-Trust Architecture',
      detail: 'Implement enterprise-grade zero-trust security frameworks that verify every user and device, ensuring maximum protection across your entire digital infrastructure.',
      icon: <Shield className="h-6 w-6" />,
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Advanced Penetration Testing',
      detail: 'Our expert red team conducts comprehensive penetration testing using cutting-edge methodologies to identify and remediate vulnerabilities before malicious actors can exploit them.',
      icon: <Eye className="h-6 w-6" />,
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Rapid Incident Response',
      detail: 'Experience industry-leading incident response times with our 24/7 security operations center, ensuring minimal business disruption and maximum threat containment.',
      icon: <Zap className="h-6 w-6" />,
      gradient: 'from-orange-500 to-red-500'
    },
    {
      title: 'Compliance & Governance',
      detail: 'Navigate complex regulatory landscapes with confidence. Our compliance experts ensure your organization meets and exceeds industry standards and requirements.',
      icon: <CheckCircle className="h-6 w-6" />,
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      title: 'Security Training & Awareness',
      detail: 'Empower your workforce with comprehensive cybersecurity training programs that transform employees into your first line of defense against cyber threats.',
      icon: <GraduationCap className="h-6 w-6" />,
      gradient: 'from-teal-500 to-green-500'
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "CISO, TechCorp",
      content: "MVPsec.ai transformed our security posture completely. Their proactive approach prevented three major breaches last quarter.",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b332c1cd?w=64&h=64&fit=crop&crop=face"
    },
    {
      name: "Michael Rodriguez",
      role: "CTO, FinanceFlow",
      content: "The best investment we've made. Their AI-powered threat detection is simply unmatched in the industry.",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=64&h=64&fit=crop&crop=face"
    },
    {
      name: "Dr. Emily Watson",
      role: "Security Director, HealthTech",
      content: "Professional, responsive, and incredibly effective. They've become an integral part of our security strategy.",
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=64&h=64&fit=crop&crop=face"
    }
  ];

  const pricingTiers = [
    {
      name: "Startup",
      price: "$999",
      period: "/month",
      description: "Perfect for growing companies",
      features: [
        "24/7 Security Monitoring",
        "Vulnerability Assessments",
        "Incident Response",
        "Compliance Support",
        "Basic Threat Intelligence"
      ],
      highlighted: false
    },
    {
      name: "Enterprise",
      price: "$4,999",
      period: "/month",
      description: "For established businesses",
      features: [
        "Everything in Startup",
        "Advanced Threat Hunting",
        "Red Team Exercises",
        "Custom Security Policies",
        "Dedicated Security Analyst",
        "Priority Support"
      ],
      highlighted: true
    },
    {
      name: "Enterprise+",
      price: "Custom",
      period: "",
      description: "For large organizations",
      features: [
        "Everything in Enterprise",
        "AI-Powered Analytics",
        "Custom Integrations",
        "On-site Security Audits",
        "Executive Reporting",
        "White-label Solutions"
      ],
      highlighted: false
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>MVPsec.ai - Enterprise Cybersecurity Solutions | AI-Powered Security</title>
        <meta name="description" content="Leading enterprise cybersecurity company providing AI-powered threat detection, penetration testing, incident response, and comprehensive security solutions for Fortune 500 companies." />
        <meta name="keywords" content="cybersecurity, enterprise security, AI security, threat detection, penetration testing, incident response, compliance, risk management" />
        <link rel="canonical" href="https://mvpsec.ai/" />

        {/* Open Graph */}
        <meta property="og:title" content="MVPsec.ai - Enterprise Cybersecurity Solutions" />
        <meta property="og:description" content="AI-powered security solutions that protect Fortune 500 companies from the most sophisticated cyber threats. Trusted by 500+ enterprises worldwide." />
        <meta property="og:url" content="https://mvpsec.ai/" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content="MVPsec.ai - Enterprise Cybersecurity Solutions" />
        <meta name="twitter:description" content="AI-powered security solutions that protect Fortune 500 companies from the most sophisticated cyber threats." />

        {/* JSON-LD for Homepage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "MVPsec.ai - Enterprise Cybersecurity Solutions",
            "description": "Leading enterprise cybersecurity company providing AI-powered threat detection, penetration testing, incident response, and comprehensive security solutions for Fortune 500 companies.",
            "url": "https://mvpsec.ai/",
            "mainEntity": {
              "@type": "Organization",
              "name": "MVPsec.ai",
              "url": "https://mvpsec.ai"
            }
          })}
        </script>
      </Helmet>
      {/* Hero Section - Billion Dollar Premium */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-secondary-900 to-dark-950"></div>
        <motion.div
          style={{ y: y1 }}
          className="absolute inset-0 opacity-30"
        >
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent-500/20 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-display font-bold leading-tight">
              <span className="bg-gradient-to-r from-white via-primary-200 to-accent-300 bg-clip-text text-transparent">
                Enterprise
              </span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 via-accent-400 to-primary-300 bg-clip-text text-transparent">
                Cybersecurity
              </span>
            </h1>
            <p className="mt-8 text-xl sm:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              AI-powered security solutions that protect Fortune 500 companies from the most sophisticated cyber threats.
              <span className="text-accent-400 font-semibold"> Trusted by 500+ enterprises worldwide.</span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = '/contact'}
              className="group px-8 py-4 bg-gradient-to-r from-primary-600 to-accent-600 text-white font-semibold rounded-2xl shadow-2xl hover:shadow-cyber transition-all duration-300 flex items-center space-x-2 cursor-pointer"
            >
              <span>Start Free Trial</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.open('https://calendly.com/mvpsec-demo', '_blank')}
              className="group px-8 py-4 border-2 border-white/20 text-white font-semibold rounded-2xl backdrop-blur-sm hover:bg-white/10 transition-all duration-300 flex items-center space-x-2 cursor-pointer"
            >
              <Play className="h-5 w-5" />
              <span>Watch Demo</span>
            </motion.button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { label: "Enterprise Clients", value: stats.clients, suffix: "+" },
              { label: "Threats Stopped", value: stats.threats.toLocaleString(), suffix: "+" },
              { label: "Uptime SLA", value: stats.uptime, suffix: "%" },
              { label: "Response Time", value: stats.response, suffix: "s" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-accent-400">
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-gray-400 mt-2">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 dark:from-dark-950 dark:to-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-display font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent mb-6">
              Advanced Security Solutions
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Comprehensive cybersecurity services designed for the modern enterprise
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {strategies.map((strategy, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                onClick={() => handleStrategyClick(index)}
                className="group cursor-pointer"
              >
                <div className="relative p-8 bg-white dark:bg-dark-800 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-800 hover:border-primary-200 dark:hover:border-primary-800">
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${strategy.gradient} text-white mb-6`}>
                    {strategy.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {strategy.title}
                  </h3>
                  <AnimatePresence mode="wait">
                    {selectedStrategyIndex === index ? (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-gray-600 dark:text-gray-400 leading-relaxed"
                      >
                        {strategy.detail}
                      </motion.p>
                    ) : (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-gray-500 dark:text-gray-500 text-sm"
                      >
                        Click to learn more →
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-dark-900 dark:to-dark-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-display font-bold text-gray-900 dark:text-white mb-6">
              Trusted by Industry Leaders
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              See what security professionals say about our solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-dark-800 p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full mr-4"
                  />
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-white dark:bg-dark-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-display font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent mb-6">
              Enterprise Pricing
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Scalable security solutions for every business size
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative p-8 rounded-3xl transition-all duration-300 ${
                  tier.highlighted
                    ? 'bg-gradient-to-br from-primary-600 to-accent-600 text-white scale-105 shadow-2xl'
                    : 'bg-gray-50 dark:bg-dark-800 hover:shadow-xl'
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-accent-400 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className={`text-2xl font-bold mb-2 ${
                    tier.highlighted ? 'text-white' : 'text-gray-900 dark:text-white'
                  }`}>
                    {tier.name}
                  </h3>
                  <p className={`text-sm ${
                    tier.highlighted ? 'text-primary-100' : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {tier.description}
                  </p>
                  <div className="mt-4">
                    <span className={`text-4xl font-bold ${
                      tier.highlighted ? 'text-white' : 'text-gray-900 dark:text-white'
                    }`}>
                      {tier.price}
                    </span>
                    <span className={`text-lg ${
                      tier.highlighted ? 'text-primary-100' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {tier.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className={`h-5 w-5 mr-3 ${
                        tier.highlighted ? 'text-white' : 'text-accent-500'
                      }`} />
                      <span className={
                        tier.highlighted ? 'text-primary-100' : 'text-gray-600 dark:text-gray-400'
                      }>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.location.href = '/contact'}
                  className={`w-full py-3 px-6 rounded-2xl font-semibold transition-all duration-300 cursor-pointer ${
                    tier.highlighted
                      ? 'bg-white text-primary-600 hover:bg-gray-100'
                      : 'bg-gradient-to-r from-primary-600 to-accent-600 text-white hover:shadow-glow'
                  }`}
                >
                  Get Started
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary-900 via-secondary-900 to-dark-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl font-display font-bold text-white mb-6">
              Ready to Secure Your Future?
            </h2>
            <p className="text-xl text-gray-300 mb-12 leading-relaxed">
              Join 500+ enterprises who trust MVPsec.ai to protect their most valuable assets.
              Start your journey to unbreachable security today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.href = '/contact'}
                className="px-8 py-4 bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold rounded-2xl shadow-2xl hover:shadow-cyber transition-all duration-300 cursor-pointer"
              >
                Start Free 30-Day Trial
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://calendly.com/mvpsec-demo', '_blank')}
                className="px-8 py-4 border-2 border-white/20 text-white font-semibold rounded-2xl backdrop-blur-sm hover:bg-white/10 transition-all duration-300 cursor-pointer"
              >
                Schedule Demo
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};
  export default HomePage;
  